import { FrontendConstants } from '../common/FrontendConstants';
import { Settings } from '../common/Settings';
import { AuthClient } from '../exports';
import { CommonConstants } from '@shared/common';
import type { ICoreKnowledgeDocumentDTO, INicheContentDTO, LocalizationCode } from '@shared/common';

/** Multipart form field name the billing service expects for an uploaded core knowledge file. */
const localConstants = { fileField: 'file' } as const;

/** Request to create a niche-content catalog entry (Portal operator only). */
export interface ICreateNicheRequest {
  niche: string;
  signupLabel: string;
  eyebrowLabel?: string;
  tickerLabel?: string;
  leadsLabel?: string;
  bookingsLabel?: string;
  avgJobValue?: number;
  metadata?: Record<string, string>;
  showInHero?: boolean;
  showInTicker?: boolean;
  showInRoi?: boolean;
  sortOrder?: number;
  active?: boolean;
  allowCustomTerms?: boolean;
}

/** Request to apply a partial update to a niche-content entry (Portal operator only). Slug and protected flag are immutable. */
export interface IUpdateNicheRequest {
  signupLabel?: string;
  eyebrowLabel?: string | null;
  tickerLabel?: string | null;
  leadsLabel?: string | null;
  bookingsLabel?: string | null;
  avgJobValue?: number | null;
  metadata?: Record<string, string>;
  showInHero?: boolean;
  showInTicker?: boolean;
  showInRoi?: boolean;
  sortOrder?: number;
  active?: boolean;
  allowCustomTerms?: boolean;
}

/**
 * Client for the niche-content catalog: the public marketing-landing read and cross-tenant admin CRUD.
 *
 * All calls route through the billing service (`anon/niche-content` for the landing, `admin/niche-content` for Portal operators).
 */
export class NicheContentClient {
  /** Singleton instance. */
  private static instance: NicheContentClient;
  /** Prevent external instantiation. */
  private constructor() {}

  /** @returns The singleton NicheContentClient instance. */
  public static GetInstance(): NicheContentClient {
    return NicheContentClient.instance ?? (NicheContentClient.instance = new NicheContentClient());
  }

  /**
   * GET /api/billing/anon/niche-content - Public catalog of active niches for the marketing landing (no auth).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param lang - Target language for niche display copy. Defaults to the source language.
   * @returns The active niches, ascending by sort order.
   */
  public async ListPublicNiches(signal: AbortSignal, lang: LocalizationCode = CommonConstants.Defaults.Localization): Promise<INicheContentDTO[]> {
    const params = new URLSearchParams({ [CommonConstants.Params.Lang]: lang });
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `anon/niche-content?${params}`, { signal });
    if (!res.ok) throw new Error('Failed to fetch niche content');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/niche-content - List every niche-content entry, active and inactive (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns All niche-content entries.
   */
  public async ListAdminNiches(signal: AbortSignal): Promise<INicheContentDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/niche-content', { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch niche content');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/clear/niche-content - Flush the niche-content Redis cache (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @returns 'CLEARED' confirmation string.
   */
  public async ClearNiches(signal: AbortSignal): Promise<string> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, 'admin/clear/niche-content', { signal }, auth);
    if (!res.ok) throw new Error('Failed to clear niche-content cache');
    return await res.text();
  }

  /**
   * POST /api/billing/admin/niche-content - Create a niche-content entry (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param body - The niche creation payload.
   * @returns The created niche-content entry.
   */
  public async CreateAdminNiche(signal: AbortSignal, body: ICreateNicheRequest): Promise<INicheContentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      'admin/niche-content',
      {
        method: FrontendConstants.MethodPost,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to create niche content');
    return await res.json();
  }

  /**
   * PUT /api/billing/admin/niche-content/:nicheID - Apply a partial update to a niche-content entry (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheID - The niche to update.
   * @param body - The partial update payload.
   * @returns The updated niche-content entry.
   */
  public async UpdateAdminNiche(signal: AbortSignal, nicheID: string, body: IUpdateNicheRequest): Promise<INicheContentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `admin/niche-content/${nicheID}`,
      {
        method: FrontendConstants.MethodPut,
        headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson },
        body: JSON.stringify(body),
        signal
      },
      auth
    );
    if (!res.ok) throw new Error('Failed to update niche content');
    return await res.json();
  }

  /**
   * DELETE /api/billing/admin/niche-content/:nicheID - Delete a niche-content entry (Portal operator only). Rejected when protected, in use, or below the minimum-active floor.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheID - The niche to delete.
   * @returns An object indicating whether a row was deleted.
   */
  public async DeleteAdminNiche(signal: AbortSignal, nicheID: string): Promise<{ deleted: boolean }> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/niche-content/${nicheID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to delete niche content');
    return await res.json();
  }

  /**
   * GET /api/billing/admin/niche-content/:nicheSlug/knowledge - List a niche's core knowledge documents (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @returns The niche's core knowledge documents.
   */
  public async ListCoreKnowledge(signal: AbortSignal, nicheSlug: string): Promise<ICoreKnowledgeDocumentDTO[]> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/niche-content/${nicheSlug}/knowledge`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch core knowledge documents');
    return await res.json();
  }

  /**
   * POST /api/billing/admin/niche-content/:nicheSlug/knowledge - Upload a core knowledge document (multipart) for a niche (Portal operator only).
   *
   * The browser sets the multipart `Content-Type` boundary automatically, so no content-type header is supplied.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @param file - The file to upload.
   * @param title - Optional document title; defaults to the file name server-side when omitted.
   * @returns The created core knowledge document.
   */
  public async UploadCoreKnowledge(signal: AbortSignal, nicheSlug: string, file: File, title?: string): Promise<ICoreKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const form = new FormData();
    form.append(localConstants.fileField, file);
    if (title) form.append('title', title);
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/niche-content/${nicheSlug}/knowledge`, { method: FrontendConstants.MethodPost, body: form, signal }, auth);
    if (!res.ok) throw new Error('Failed to upload core knowledge document');
    return await res.json();
  }

  /**
   * POST /api/billing/admin/niche-content/:nicheSlug/knowledge/manual - Create a manually-authored core knowledge document from a typed title and body (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @param title - The document title.
   * @param body - The multiline body text.
   * @returns The created core knowledge document.
   */
  public async CreateCoreKnowledge(signal: AbortSignal, nicheSlug: string, title: string, body: string): Promise<ICoreKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `admin/niche-content/${nicheSlug}/knowledge/manual`,
      { method: FrontendConstants.MethodPost, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to create core knowledge document');
    return await res.json();
  }

  /**
   * PUT /api/billing/admin/niche-content/:nicheSlug/knowledge/:documentID/manual - Update a manually-authored core knowledge document (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @param documentID - The document to update.
   * @param title - The new document title.
   * @param body - The new multiline body text.
   * @returns The updated core knowledge document.
   */
  public async UpdateCoreKnowledge(signal: AbortSignal, nicheSlug: string, documentID: string, title: string, body: string): Promise<ICoreKnowledgeDocumentDTO> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(
      Settings.SERVICE_BILLING_URI,
      `admin/niche-content/${nicheSlug}/knowledge/${documentID}/manual`,
      { method: FrontendConstants.MethodPut, headers: { [FrontendConstants.HeaderContentType]: FrontendConstants.ContentTypeJson }, body: JSON.stringify({ title, body }), signal },
      auth
    );
    if (!res.ok) throw new Error('Failed to update core knowledge document');
    return await res.json();
  }

  /**
   * DELETE /api/billing/admin/niche-content/:nicheSlug/knowledge/:documentID - Remove a niche's core knowledge document (Portal operator only).
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @param documentID - The document to remove.
   */
  public async DeleteCoreKnowledge(signal: AbortSignal, nicheSlug: string, documentID: string): Promise<void> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/niche-content/${nicheSlug}/knowledge/${documentID}`, { method: FrontendConstants.MethodDelete, signal }, auth);
    if (!res.ok) throw new Error('Failed to delete core knowledge document');
  }

  /**
   * GET /api/billing/admin/niche-content/:nicheSlug/knowledge/:documentID/content - Fetch a core knowledge document's original bytes (Portal operator only).
   *
   * Returned as a Blob (typed by the server's Content-Type) so callers can open it in a new tab via an object URL - the endpoint is auth-gated, so a plain `<a href>` cannot carry the bearer
   * token.
   *
   * @param signal - AbortSignal to cancel the request if needed.
   * @param nicheSlug - The owning business niche slug.
   * @param documentID - The document whose content to fetch.
   * @returns The document content as a typed Blob.
   */
  public async GetCoreKnowledgeContent(signal: AbortSignal, nicheSlug: string, documentID: string): Promise<Blob> {
    const auth = await AuthClient.GetAuthClientAdapter();
    const res = await AuthClient.Fetch(Settings.SERVICE_BILLING_URI, `admin/niche-content/${nicheSlug}/knowledge/${documentID}/content`, { signal }, auth);
    if (!res.ok) throw new Error('Failed to fetch core knowledge document content');
    return await res.blob();
  }
}
